import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RecentPostsSidebar from '../components/RecentPostsSidebar';
import { parseMarkdown } from '../utils/markdownParser';

function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/blogs/${id}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
      if (error.response?.status === 404) {
        navigate('/blog');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <h2>Blog not found</h2>
          <Link to="/blog" className="btn btn-primary mt-3">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div
        className="container-fluid"
        style={{
          background: 'linear-gradient(rgba(0, 61, 130, 0.9), rgba(0, 61, 130, 0.9)), url(/img/hospital-building.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '4rem 0 3rem',
        }}
      >
        <div className="container">
          <div className="text-center">
            <h2 className="text-white mb-3" style={{ fontSize: '2.5rem', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif", textTransform: 'uppercase', letterSpacing: '1px' }}>
              TGH BLOG
            </h2>
            <nav aria-label="breadcrumb" className="mt-3 d-flex justify-content-center">
              <ol className="breadcrumb mb-0" style={{ backgroundColor: 'transparent', padding: 0 }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/blog" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Blog
                  </Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">
                  Article
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Blog Content */}
      <section className="py-5" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <div className="row tgh-page-with-sidebar">
            <div className="col-xl-9 col-lg-8 col-12">
              <article className="tgh-news-article">
                {/* Category Badge */}
                {blog.category && (
                  <span className="badge bg-secondary mb-3" style={{ fontSize: '0.85rem' }}>
                    {blog.category}
                  </span>
                )}

                {/* Title */}
                <h1 className="display-4 mb-4" style={{ color: '#1a1a1a', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif" }}>
                  {blog.title}
                </h1>

                {/* Meta Information */}
                <div className="mb-4 text-muted" style={{ fontSize: '0.95rem', borderBottom: '1px solid #e0e0e0', paddingBottom: '1rem' }}>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <span>
                      <i className="bi bi-person me-2"></i>
                      <strong>Author:</strong> {blog.author}
                    </span>
                    <span>
                      <i className="bi bi-calendar3 me-2"></i>
                      <strong>Published:</strong> {formatDate(blog.created_at)}
                    </span>
                  </div>
                </div>

                {/* Featured image — same frame sizing as TGH News post carousel */}
                {blog.image && (
                  <div className="mb-4 news-post-carousel">
                    <div className="news-carousel-image-container single-image">
                      <img
                        src={blog.image ? (blog.image.startsWith('/uploads/') ? blog.image : `/uploads/${blog.image}`) : '/img/Gallery/opd1.jpg'}
                        alt={blog.title}
                        className="news-carousel-image"
                        loading="eager"
                        decoding="async"
                        onError={(e) => {
                          e.target.src = '/img/Gallery/opd1.jpg';
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Content */}
                <div
                  className="blog-content tgh-news-post-body"
                  style={{
                    fontSize: '1.1rem',
                    lineHeight: '1.8',
                    color: '#333',
                    marginBottom: '3rem',
                    overflow: 'visible',
                    textAlign: 'justify',
                  }}
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(blog.content) }}
                />
              </article>
            </div>
            
            {/* Sidebar */}
            <div className="col-xl-3 col-lg-4 col-12 tgh-sidebar-col">
              <div style={{ position: 'sticky', top: '20px', zIndex: 10 }}>
                <RecentPostsSidebar type="blogs" limit={5} excludeId={id} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogDetail;
